import { useState, useCallback, useEffect } from 'react';
import { Lookup, LookupOption } from '@/components/ui/lookup';
import { useEntities, useEntity } from '@/lib/hooks/useEntities';
import type { ApiEntity, EntitiesQuery } from '@/lib/api/types';

interface EntityLookupProps {
  value?: LookupOption<ApiEntity> | null;
  onChange: (value: LookupOption<ApiEntity> | null) => void;
  entityType?: 'brand' | 'product' | 'collection';
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  excludeIds?: number[];
  /** When provided and no value is set, fetches the entity by ID and auto-selects it */
  initialId?: number | string;
}

export function EntityLookup({
  value,
  onChange,
  entityType,
  placeholder = 'Search entities...',
  className,
  disabled,
  excludeIds = [],
  initialId,
}: EntityLookupProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  // Fetch entity by initialId when no value is provided
  const shouldFetchInitial = !!initialId && !value;
  const { data: initialEntity } = useEntity(shouldFetchInitial ? String(initialId) : '');

  // Auto-select the fetched entity
  useEffect(() => {
    if (initialEntity && !value) {
      onChange({
        id: initialEntity.id,
        label: initialEntity.name_1 || `Entity ${initialEntity.id}`,
        data: initialEntity as ApiEntity,
      });
    }
  }, [initialEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryFilters: EntitiesQuery = {
    search: searchQuery || undefined,
    type: entityType ? [entityType] : undefined,
    limit: 20,
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useEntities(
    queryFilters,
    { enabled: isEnabled }
  );

  const allEntities = data?.pages.flatMap((page) => page.entities) || [];

  // Filter out excluded IDs
  const filteredEntities = allEntities.filter((entity) => !excludeIds.includes(entity.id));

  const options: LookupOption<ApiEntity>[] = filteredEntities.map((entity) => ({
    id: entity.id,
    label: entity.name_1 || `Entity ${entity.id}`,
    data: entity,
  }));

  const handleOpen = useCallback(() => {
    setIsEnabled(true);
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Lookup
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      onOpen={handleOpen}
      options={options}
      isLoading={isLoading || isFetchingNextPage}
      hasMore={hasNextPage}
      placeholder={placeholder}
      emptyMessage="No entities found"
      className={className}
      disabled={disabled}
    />
  );
}
